/**
 * HUD-Teil: Auto-Lauf, Anker, Automatikgrad, Zoom.
 *
 * Der **Automatikgrad** ist der Kern der Rückmeldung zur Automation
 * (Ueberarbeitung B9a): Er entscheidet, ob Fähigkeiten, die den Spieler
 * versetzen, von selbst feuern dürfen. Standard ist „umsichtig" — sie tun es
 * nicht, denn Position ist die Verteidigung des Spielers.
 */

import type { Schnappschuss } from '@pg/protocol';
import {
  AUTOMATIK_TEXT,
  TEMPO_TEXT,
  einstellungen,
  naechsterAutomatikgrad,
  naechstesTempoprofil,
  naechsterZoom,
  setzeAutomatikgrad,
  setzeTempoprofil,
  setzeZoom,
} from '../einstellungen.js';
import { el, type HudTeil, type HudUmgebung } from './typen.js';

export const schalter: HudTeil = {
  name: 'schalter',

  bauen(u: HudUmgebung) {
    const autolauf = el<HTMLButtonElement>('#autolauf');
    const anker = el<HTMLButtonElement>('#anker');
    const automatik = el<HTMLButtonElement>('#automatik');
    const zoom = el<HTMLButtonElement>('#zoom');
    const gangart = el<HTMLButtonElement>('#gangart');

    // Nur einmal verdrahten, auch wenn `bauen` bei jedem Abstieg laeuft.
    if (autolauf.dataset['verdrahtet']) return;
    autolauf.dataset['verdrahtet'] = '1';

    autolauf.addEventListener('click', () => u.steuerung.umschaltenAutolauf());
    anker.addEventListener('click', () => u.steuerung.umschaltenAnker());

    automatik.addEventListener('click', () => {
      const grad = naechsterAutomatikgrad();
      setzeAutomatikgrad(grad);
      u.steuerung.setzeAutomatikgrad(grad);
      hinweis(AUTOMATIK_TEXT[grad].lang);
    });

    zoom.addEventListener('click', () => {
      const stufe = naechsterZoom();
      setzeZoom(stufe);
      hinweis(`Sicht: ${stufe}`);
    });

    // Gangart: die eigentliche Frage aus dem zweiten Spieltest. Umschaltbar im
    // laufenden Spiel, damit man den Unterschied FUEHLEN kann statt ihn zu lesen.
    gangart.addEventListener('click', () => {
      const p = naechstesTempoprofil();
      setzeTempoprofil(p);
      u.steuerung.setzeTempoprofil(p);
      hinweis(`${TEMPO_TEXT[p]?.kurz ?? p}: ${TEMPO_TEXT[p]?.lang ?? ''}`);
    });
  },

  aktualisieren(u: HudUmgebung, s: Schnappschuss) {
    el<HTMLButtonElement>('#autolauf').setAttribute('aria-pressed', String(s.spieler.autolauf));
    el<HTMLButtonElement>('#anker').setAttribute('aria-pressed', String(s.spieler.anker));

    const automatik = el<HTMLButtonElement>('#automatik');
    const grad = s.spieler.automatikgrad;
    (automatik.querySelector('.wert') as HTMLElement).textContent = AUTOMATIK_TEXT[grad].kurz;
    automatik.dataset['grad'] = grad;

    (el<HTMLButtonElement>('#zoom').querySelector('.wert') as HTMLElement).textContent =
      einstellungen().zoom;

    const gangart = el<HTMLButtonElement>('#gangart');
    (gangart.querySelector('.wert') as HTMLElement).textContent =
      TEMPO_TEXT[s.spieler.tempoprofil]?.kurz ?? s.spieler.tempoprofil;
    gangart.dataset['profil'] = s.spieler.tempoprofil;

    // Die Simulation fuehrt den Zustand — die Steuerung zieht nach.
    u.steuerung.uebernimm(s.spieler.autolauf, s.spieler.anker);
  },
};

let hinweisZeit: ReturnType<typeof setTimeout> | null = null;

/** Kurzer Hinweis über der Bedienung. Erklärt, was ein Schalter gerade tat. */
export function hinweis(text: string): void {
  const e = el<HTMLDivElement>('#hinweis');
  e.textContent = text;
  e.classList.add('sichtbar');
  if (hinweisZeit) clearTimeout(hinweisZeit);
  hinweisZeit = setTimeout(() => e.classList.remove('sichtbar'), 2600);
}
